import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

const URL = "https://www.travelbackland.com/tours";
const BOOKEO_SRC = "https://bookeo.com/widget.js?a=41569PMP4NA1862D957032";
const BOOKEO_POSITION_ID = "bookeo_position";

type BookeoWindow = Window &
  typeof globalThis & {
    bookeo_start?: () => void;
    axiomct_project?: unknown | null;
    axiomct_socket?: { destroy?: () => void } | null;
    axiomct_div?: HTMLElement | null;
    axiomct_iframe?: HTMLIFrameElement | null;
    axiomct_spinner?: HTMLElement | null;
    axiomct_loadStarted?: boolean | null;
    axiomct_initTime?: Date | null;
  };

function removeStrayBookeoNodes(keep?: HTMLElement | null) {
  ["axiomct_div", "axiomct_iframe", "axiomct_spinner", BOOKEO_POSITION_ID].forEach(
    (id) => {
      document.querySelectorAll(`#${id}`).forEach((node) => {
        if (node !== keep && !keep?.contains(node)) {
          node.parentElement?.removeChild(node);
        }
      });
    },
  );
}

function resetBookeo(el?: HTMLElement | null) {
  const w = window as BookeoWindow;
  try {
    w.axiomct_socket?.destroy?.();
  } catch {
    /* noop */
  }
  if (el) el.innerHTML = "";
  removeStrayBookeoNodes(el);
  w.axiomct_project = null;
  w.axiomct_socket = null;
  w.axiomct_div = null;
  w.axiomct_iframe = null;
  w.axiomct_spinner = null;
  w.axiomct_loadStarted = null;
  w.axiomct_initTime = null;
}

function loadAndStartBookeo(el: HTMLElement) {
  resetBookeo(el);
  const existing = document.querySelector<HTMLScriptElement>(
    'script[src*="bookeo.com/widget.js"]',
  );
  if (existing) {
    // Script already loaded — manually re-init into our container.
    const w = window as BookeoWindow;
    if (typeof w.bookeo_start === "function") {
      try {
        w.bookeo_start();
      } catch {
        /* noop */
      }
    }
    return;
  }
  // First load — the script auto-initializes itself, do not call bookeo_start.
  const script = document.createElement("script");
  script.type = "text/javascript";
  script.src = BOOKEO_SRC;
  script.async = true;
  script.dataset.bookeoManaged = "true";
  document.body.appendChild(script);
}

export const Route = createFileRoute("/tours")({
  head: () => ({
    meta: [
      { title: "Guided Tours — Backland" },
      {
        name: "description",
        content:
          "Browse and book guided tour options available during your stay at Backland.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Guided Tours — Backland" },
      { property: "og:url", content: URL },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: ToursPage,
});

function ToursPage() {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const timer = window.setTimeout(() => loadAndStartBookeo(el), 50);
    return () => {
      window.clearTimeout(timer);
      resetBookeo(el);
    };
  }, []);

  return (
    <div
      className="fixed inset-0 overflow-y-auto overscroll-contain no-scrollbar bg-background text-foreground"
      style={{ touchAction: "auto", overscrollBehavior: "auto" }}
    >
      <header className="relative w-full max-w-3xl mx-auto px-6 pt-14 md:pt-20 pb-8 text-center">
        <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-4">
          For Registered Guests
        </div>
        <h1 className="font-serif text-3xl md:text-5xl leading-[1.1] text-foreground">
          See Our Guided Tour Options
        </h1>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
        <div
          ref={containerRef}
          id={BOOKEO_POSITION_ID}
          className="bookeo-widget relative z-0 rounded-xl sm:border sm:border-foreground/10 bg-background p-0 sm:p-4 w-full flex justify-center"
        />
      </main>
    </div>
  );
}
